import { AwsRegion } from "types/generated";
import { AllowedRegion, getRegion } from "utils/region";

export type RegionOption = Pick<AwsRegion, "id" | "location" | "region">;

export type RegionItem = { value: string; label: string };
export type RegionSection = { name: string; label: string; items: RegionItem[] };

const EUROPE_SECTION_PREFIX = "Europe";
const US_SECTION_PREFIX = "US";

// EU accounts most often scan EU regions but frequently also US ones (and vice versa for
// everyone else), so both get bubbled up — just in a different order — ahead of the rest.
const getPriorityPrefixes = (region: AllowedRegion) =>
  region === "eu"
    ? [EUROPE_SECTION_PREFIX, US_SECTION_PREFIX]
    : [US_SECTION_PREFIX, EUROPE_SECTION_PREFIX];

export const sortSectionsByPriority = (
  sections: RegionSection[],
  region: AllowedRegion
): RegionSection[] => {
  const priorityPrefixes = getPriorityPrefixes(region);

  const prioritized = priorityPrefixes.flatMap((prefix) =>
    sections.filter((section) => section.label.startsWith(prefix))
  );

  const rest = sections.filter(
    (section) => !priorityPrefixes.some((prefix) => section.label.startsWith(prefix))
  );

  return [...prioritized, ...rest];
};

export const groupRegionsIntoSections = (options: RegionOption[]): RegionSection[] => {
  const bySection = new Map<string, RegionItem[]>();

  options.forEach((option) => {
    const items = bySection.get(option.region) ?? [];
    items.push({ value: option.id, label: `${option.location} (${option.id})` });
    bySection.set(option.region, items);
  });

  const sections = Array.from(bySection.entries()).map(([label, items]) => ({
    name: label,
    label,
    items,
  }));

  return sortSectionsByPriority(sections, getRegion());
};

// react-aria's own recursive filtering hangs on nested/sectioned collections, so RegionsField
// controls its search input and filters sections itself instead of relying on it.
export const filterRegionSections = (sections: RegionSection[], query: string): RegionSection[] => {
  const normalized = query.trim().toLowerCase();

  if (!normalized) {
    return sections;
  }

  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) =>
          item.label.toLowerCase().includes(normalized) ||
          item.value.toLowerCase().includes(normalized)
      ),
    }))
    .filter((section) => section.items.length > 0);
};
